import { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Layout } from "./components/layout";
import { RouteSEO } from "./components/route-seo";
import { ScrollToTop } from "./components/scroll-to-top";
import { CookieConsent } from "./components/cookie-consent";
import { GoogleTagManager } from "./components/google-tag-manager";
import { getLocaleFromPathname } from "./lib/i18n";
import { getAllLocalizedRoutes, getNestedLocalizedRoutes } from "./lib/routes";
import Index from "./pages/Index";

// Lazy-loaded routes (code-split to reduce initial bundle size)
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const Leitbild = lazy(() => import("./pages/Leitbild"));
const Team = lazy(() => import("./pages/Team"));
const Partners = lazy(() => import("./pages/Partners"));
const Sustainability = lazy(() => import("./pages/Sustainability"));
const Careers = lazy(() => import("./pages/Careers"));
const JobOpenings = lazy(() => import("./pages/JobOpenings"));
const VocationalTraining = lazy(() => import("./pages/VocationalTraining"));
const News = lazy(() => import("./pages/News"));
const Contact = lazy(() => import("./pages/Contact"));
const Anfahrt = lazy(() => import("./pages/Anfahrt"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Schaltschrankbau = lazy(() => import("./pages/services/Schaltschrankbau"));
const HardwareSoftwareEngineering = lazy(() => import("./pages/services/HardwareSoftwareEngineering"));
const SoftwareEngineering = lazy(() => import("./pages/services/SoftwareEngineering"));
const HardwareentwicklerJob = lazy(() => import("./pages/HardwareentwicklerJob"));
const ProjektleiterJob = lazy(() => import("./pages/ProjektleiterJob"));
const ChameleonStory = lazy(() => import("./pages/ChameleonStory"));
const AutomatikerJob = lazy(() => import("./pages/AutomatikerJob"));
const Imprint = lazy(() => import("./pages/Imprint"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Documents = lazy(() => import("./pages/Documents"));
const QrRedirect = lazy(() => import("./pages/QrRedirect"));
const Speedpainting = lazy(() => import("./pages/Speedpainting"));
const Digitalisierungscheck = lazy(() => import("./pages/Digitalisierungscheck"));
const StriebigAg = lazy(() => import("./pages/kunden/StriebigAg"));
const Kundenportal = lazy(() => import("./pages/kunden/Kundenportal"));
const LegacyRedirect = lazy(() => import("./pages/LegacyRedirect"));
import { PortalGuard } from "@/components/kunden/portal-guard";
import { MobileRundgangGuard } from "@/components/rundgang/mobile-rundgang-guard";
const PortalStart = lazy(() => import("./pages/kunden/PortalStart"));
const RundgangHome = lazy(() => import("./pages/rundgang/RundgangHome"));
const RundgangBereiche = lazy(() => import("./pages/rundgang/RundgangBereiche"));
const RundgangTechnologien = lazy(() => import("./pages/rundgang/RundgangTechnologien"));
const RundgangLeistungen = lazy(() => import("./pages/rundgang/RundgangLeistungen"));
const RundgangKarriere = lazy(() => import("./pages/rundgang/RundgangKarriere"));
const RundgangZukunft = lazy(() => import("./pages/rundgang/RundgangZukunft"));
const RundgangDetail = lazy(() => import("./pages/rundgang/RundgangDetail"));
const TreffpunktWerkHome = lazy(() => import("./pages/rundgang/TreffpunktWerkHome"));
const TreffpunktWerkStation = lazy(() => import("./pages/rundgang/TreffpunktWerkStation"));

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const isAllowedMobileRundgangPage =
    location.pathname === "/rundgang" ||
    location.pathname === "/rundgang/" ||
    location.pathname.startsWith("/rundgang/hallenplan");

  if (isMobile && location.pathname.startsWith("/rundgang/") && !isAllowedMobileRundgangPage) {
    return <Navigate to="/rundgang" replace />;
  }

  const createRoutes = (paths: string[], component: JSX.Element) => {
    return paths.map(path => (
      <Route key={path} path={path} element={component} />
    ));
  };

  return (
    <>
      <RouteSEO />
      <GoogleTagManager />
      <ScrollToTop />
      <Layout locale={locale}>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/:locale" element={<Index />} />
        
        {/* Services routes */}
        {createRoutes(getAllLocalizedRoutes('services'), <Services />)}
        
        {/* Products routes – Seiten deaktiviert, Weiterleitung auf Startseite */}
        {createRoutes(getAllLocalizedRoutes('products'), <Navigate to="/" replace />)}
        {createRoutes(getNestedLocalizedRoutes('products', 'athex'), <Navigate to="/" replace />)}
        {createRoutes(getNestedLocalizedRoutes('products', 'panelscout'), <Navigate to="/" replace />)}
        
        {/* About routes */}
        {createRoutes(getAllLocalizedRoutes('about'), <About />)}
        
        {/* Leitbild routes */}
        {createRoutes(getNestedLocalizedRoutes('about', 'leitbild'), <Leitbild />)}
        
        {/* Team routes */}
        {createRoutes(getNestedLocalizedRoutes('about', 'team'), <Team />)}
        
        {/* Partners routes */}
        {createRoutes(getNestedLocalizedRoutes('about', 'partners'), <Partners />)}
        
        {/* Anfahrt routes */}
        {createRoutes(getNestedLocalizedRoutes('about', 'anfahrt'), <Anfahrt />)}
        
        {/* Chameleon Story routes */}
        {createRoutes(getNestedLocalizedRoutes('about', 'chameleonStory'), <ChameleonStory />)}
        
        {/* Success Stories inkl. Case Studies – deaktiviert, Weiterleitung auf Startseite */}
        {createRoutes(getNestedLocalizedRoutes('about', 'successStories'), <Navigate to="/" replace />)}
        {getNestedLocalizedRoutes('about', 'successStories').map(route => (
          <Route key={`${route}/*`} path={`${route}/*`} element={<Navigate to="/" replace />} />
        ))}
        
        {/* Sustainability routes */}
        {createRoutes(getAllLocalizedRoutes('sustainability'), <Sustainability />)}
        
        {/* Careers routes */}
        {createRoutes(getAllLocalizedRoutes('careers'), <Careers />)}
        
        {/* Job Openings routes */}
        {createRoutes(getNestedLocalizedRoutes('careers', 'jobOpenings'), <JobOpenings />)}
        
        {/* Vocational Training routes */}
        {createRoutes(getNestedLocalizedRoutes('careers', 'vocationalTraining'), <VocationalTraining />)}

        {/* Vocational Training Ausstellung – deaktiviert, Weiterleitung auf Startseite */}
        {getNestedLocalizedRoutes('careers', 'vocationalTraining').flatMap((parentRoute, idx) => {
          const ausstellungSlugs = ['ausstellung', 'exhibition', 'exposition', 'esposizione'];
          return (
            <Route key={`ausstellung-${idx}`} path={`${parentRoute}/${ausstellungSlugs[idx]}`} element={<Navigate to="/" replace />} />
          );
        })}
        
        {/* News routes */}
        {createRoutes(getAllLocalizedRoutes('news'), <News />)}
        
        {/* News article detail routes */}
        {getAllLocalizedRoutes('news').map(route => (
          <Route key={`${route}/:articleId`} path={`${route}/:articleId`} element={<News />} />
        ))}

        {/* Speedpainting standalone landing page (under /news/Speedpainting and locale variants) */}
        <Route path="/news/Speedpainting" element={<Speedpainting />} />
        <Route path="/en/news/Speedpainting" element={<Speedpainting />} />
        <Route path="/fr/actualites/Speedpainting" element={<Speedpainting />} />
        <Route path="/it/notizie/Speedpainting" element={<Speedpainting />} />

        {/* Lehrstart – deaktiviert, Weiterleitung auf Startseite */}
        <Route path="/lehrstart" element={<Navigate to="/" replace />} />
        <Route path="/berufsbildung/erlebnistag" element={<Navigate to="/" replace />} />

        {/* Treffpunkt Werk 2026 – Anlass vorbei, Weiterleitung auf Startseite */}
        <Route path="/Treffpunktwerk" element={<Navigate to="/" replace />} />
        <Route path="/treffpunktwerk" element={<Navigate to="/" replace />} />

        {/* all about automation 2026 – Messe vorbei, Weiterleitung auf Startseite */}
        <Route path="/messe" element={<Navigate to="/" replace />} />
        <Route path="/en/messe" element={<Navigate to="/" replace />} />
        <Route path="/fr/messe" element={<Navigate to="/" replace />} />
        <Route path="/it/messe" element={<Navigate to="/" replace />} />
        <Route path="/messe/interaktiv" element={<Navigate to="/" replace />} />
        <Route path="/messe/interaktiv/auswertung" element={<Navigate to="/" replace />} />
        <Route path="/messe/wordcloud" element={<Navigate to="/" replace />} />
        <Route path="/messe/kundenpage" element={<Navigate to="/" replace />} />
        <Route path="/karriere/interesse" element={<Navigate to="/" replace />} />

        {/* Öffentliche Digitalisierungs-Check Landingpage */}
        <Route path="/digitalisierungscheck" element={<Digitalisierungscheck />} />

        {/* Login-geschütztes Kundenportal */}
        <Route path="/kundenportal" element={<Kundenportal />} />
        <Route path="/kundenportal/start" element={<PortalStart />} />
        <Route path="/kundenportal/striebig-ag" element={<StriebigAg />} />

        {/* Digitaler Produktionsrundgang */}
        <Route path="/rundgang" element={<PortalGuard internalOnly><RundgangHome /></PortalGuard>} />
        <Route path="/rundgang/hallenplan" element={<RundgangHome initialView="plan" />} />
        <Route path="/rundgang/posten" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangBereiche /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/posten/:slug" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangDetail collection="bereiche" /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/technologien" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangTechnologien /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/technologien/:slug" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangDetail collection="technologien" /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/karriere" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangKarriere /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/zukunft" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangZukunft /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/leistungen" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangLeistungen /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/leistungen/:slug" element={<MobileRundgangGuard><PortalGuard internalOnly><RundgangDetail collection="leistungen" /></PortalGuard></MobileRundgangGuard>} />

        {/* Bereiche = interaktive Posten (vormals Treffpunkt Werk) */}
        <Route path="/rundgang/bereiche" element={<MobileRundgangGuard><PortalGuard internalOnly allowTreffpunktwerk blockBereiche><TreffpunktWerkHome /></PortalGuard></MobileRundgangGuard>} />
        <Route path="/rundgang/bereiche/:stationSlug" element={<MobileRundgangGuard><PortalGuard internalOnly allowTreffpunktwerk blockBereiche><TreffpunktWerkStation /></PortalGuard></MobileRundgangGuard>} />

        {/* Alte Adressen weiterleiten */}
        <Route path="/rundgang/treffpunktwerk" element={<Navigate to="/rundgang/bereiche" replace />} />
        <Route path="/rundgang/treffpunktwerk/:stationSlug" element={<TreffpunktWerkRedirect />} />



        {/* Contact routes */}
        {createRoutes(getAllLocalizedRoutes('contact'), <Contact />)}
        
        {/* Service sub-pages */}
        {createRoutes(getNestedLocalizedRoutes('services', 'schaltschrankbau'), <Schaltschrankbau />)}
        {createRoutes(getNestedLocalizedRoutes('services', 'engineering'), <HardwareSoftwareEngineering />)}
        {createRoutes(getNestedLocalizedRoutes('services', 'softwareengineering'), <SoftwareEngineering />)}
        
        {/* Job detail pages */}
        {/* Hardware Developer Job */}
        {getNestedLocalizedRoutes('careers', 'jobOpenings').map(route => 
          getAllLocalizedRoutes('hardwareentwickler').map(jobRoute => (
            <Route key={`${route}/${jobRoute.split('/').pop()}`} path={`${route}/${jobRoute.split('/').pop()}`} element={<HardwareentwicklerJob />} />
          ))
        ).flat()}
        
        {/* Project Manager Job */}
        {getNestedLocalizedRoutes('careers', 'jobOpenings').map(route => 
          getAllLocalizedRoutes('projektleiter').map(jobRoute => (
            <Route key={`${route}/${jobRoute.split('/').pop()}`} path={`${route}/${jobRoute.split('/').pop()}`} element={<ProjektleiterJob />} />
          ))
        ).flat()}
        
        {/* Automation Technician Job */}
        {getNestedLocalizedRoutes('careers', 'jobOpenings').map(route => 
          getAllLocalizedRoutes('automatiker').map(jobRoute => (
            <Route key={`${route}/${jobRoute.split('/').pop()}`} path={`${route}/${jobRoute.split('/').pop()}`} element={<AutomatikerJob />} />
          ))
        ).flat()}
        
        {/* Imprint routes */}
        <Route path="/imprint" element={<Imprint />} />
        <Route path="/en/imprint" element={<Imprint />} />
        <Route path="/fr/imprint" element={<Imprint />} />
        <Route path="/it/imprint" element={<Imprint />} />
        
        {/* Privacy routes */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/en/privacy" element={<Privacy />} />
        <Route path="/fr/privacy" element={<Privacy />} />
        <Route path="/it/privacy" element={<Privacy />} />
        
        {/* Documents routes */}
        {createRoutes(getAllLocalizedRoutes('documents'), <Documents />)}
        
        {/* QR-Code redirect routes */}
        <Route path="/qr-code-1" element={<QrRedirect />} />
        <Route path="/qr-code-2" element={<QrRedirect />} />
        <Route path="/qr-code-3" element={<QrRedirect />} />
        <Route path="/qr-code-4" element={<QrRedirect />} />
        <Route path="/qr-code-5" element={<QrRedirect />} />

        {/* Legacy URL redirects (old indexed paths -> current routes) */}
        {['', '/en', '/fr', '/it'].flatMap(prefix => [
          <Route key={`${prefix}/services-legacy`} path={`${prefix}/services`} element={<LegacyRedirect to="services" />} />,
          <Route key={`${prefix}/products-legacy`} path={`${prefix}/products`} element={<LegacyRedirect to="products" />} />,
          <Route key={`${prefix}/about-legacy`} path={`${prefix}/about`} element={<LegacyRedirect to="about" />} />,
          <Route key={`${prefix}/contact-legacy`} path={`${prefix}/contact`} element={<LegacyRedirect to="contact" />} />,
          <Route key={`${prefix}/team-legacy`} path={`${prefix}/team`} element={<LegacyRedirect to="about" child="team" />} />,
          <Route key={`${prefix}/management-legacy`} path={`${prefix}/management`} element={<LegacyRedirect to="about" child="team" />} />,
          <Route key={`${prefix}/jobs-legacy`} path={`${prefix}/jobs`} element={<LegacyRedirect to="careers" child="jobOpenings" />} />,
          <Route key={`${prefix}/offene-stellen-legacy`} path={`${prefix}/offene-stellen`} element={<LegacyRedirect to="careers" child="jobOpenings" />} />,
          <Route key={`${prefix}/karriere-jobs-legacy`} path={`${prefix}/karriere/jobs`} element={<LegacyRedirect to="careers" child="jobOpenings" />} />,
          <Route key={`${prefix}/karriere-arbeitgeber-legacy`} path={`${prefix}/karriere/althaus-als-arbeitgeber`} element={<LegacyRedirect to="careers" />} />,
          <Route key={`${prefix}/anfahrt-legacy`} path={`${prefix}/anfahrt`} element={<LegacyRedirect to="about" child="anfahrt" />} />,
          <Route key={`${prefix}/anfahrt-dokumente-legacy`} path={`${prefix}/anfahrt-dokumente`} element={<LegacyRedirect to="documents" />} />,
          <Route key={`${prefix}/berufsbildung-legacy`} path={`${prefix}/berufsbildung`} element={<LegacyRedirect to="careers" child="vocationalTraining" />} />,
          <Route key={`${prefix}/partners-legacy`} path={`${prefix}/partners`} element={<LegacyRedirect to="about" child="partners" />} />,
          <Route key={`${prefix}/partner-legacy`} path={`${prefix}/partner`} element={<LegacyRedirect to="about" child="partners" />} />,
          <Route key={`${prefix}/leitbild-legacy`} path={`${prefix}/leitbild`} element={<LegacyRedirect to="about" child="leitbild" />} />,
          <Route key={`${prefix}/ueberuns-legacy`} path={`${prefix}/ueberuns`} element={<LegacyRedirect to="about" />} />,
          <Route key={`${prefix}/ueberuns-team-legacy`} path={`${prefix}/ueberuns/team`} element={<LegacyRedirect to="about" child="team" />} />,
          <Route key={`${prefix}/ueberuns-leitbild-legacy`} path={`${prefix}/ueberuns/leitbild`} element={<LegacyRedirect to="about" child="leitbild" />} />,
          <Route key={`${prefix}/karriere-jobs-doubled-legacy`} path={`${prefix}/karriere/jobs/karriere/offene-stellen`} element={<LegacyRedirect to="careers" child="jobOpenings" />} />,
        ])}

        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </Layout>
    <CookieConsent />
    </>
  );
};

const TreffpunktWerkRedirect = () => {
  const { stationSlug } = useParams();
  return <Navigate to={`/rundgang/bereiche/${stationSlug ?? ""}`} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
